// Dashboard Analyse — corrélation déficit↔poids et perte théorique vs réelle.
import { useMemo, useState } from 'react';
import {
  CorrelationScatter,
  DeficitBreakdownChart,
  LossReconciliationChart,
} from '@/components/charts';
import { ChartCard, EmptyState, GranularityPicker, Kpi, PeriodPicker } from '@/components/ui';
import {
  actualLossSeries,
  adaptiveTdee,
  aggregateBy,
  consumptionNutrients,
  cumulativeDeficitBreakdownSeries,
  currentTdee,
  dailyBurned,
  deficitVsWeightChange,
  filterByPeriod,
  type Granularity,
  type Period,
  predictedLossSeries,
  round,
} from '@/domain';
import { useConsumptions, useExercises, useProfile, useWeights } from '@/hooks/queries';
import { useTheme } from '@/theme/ThemeContext';

function correlationLabel(r: number): string {
  if (Number.isNaN(r)) return 'insuffisant';
  const a = Math.abs(r);
  const strength = a >= 0.7 ? 'forte' : a >= 0.4 ? 'modérée' : a >= 0.2 ? 'faible' : 'négligeable';
  return `${strength} (${r})`;
}

export function AnalysePage() {
  const { data: consumptions = [] } = useConsumptions();
  const { data: weights = [] } = useWeights();
  const { data: exercises = [] } = useExercises();
  const { data: profile } = useProfile();
  const { palette } = useTheme();
  const [granularity, setGranularity] = useState<Granularity>('week');
  const [period, setPeriod] = useState<Period>('all');
  const phase = profile?.profile.trainingPhase ?? 'cut';

  const scopedC = useMemo(
    () => filterByPeriod(consumptions, (c) => c.datetime, period),
    [consumptions, period],
  );
  const scopedW = useMemo(() => filterByPeriod(weights, (w) => w.date, period), [weights, period]);
  const scopedE = useMemo(
    () => filterByPeriod(exercises, (e) => e.datetime, period),
    [exercises, period],
  );

  const tdeeKcal = useMemo(() => {
    if (!profile) return 0;
    const t = currentTdee(profile.profile, weights);
    return Number.isFinite(t) ? t : 0;
  }, [profile, weights]);

  const adaptive = useMemo(() => adaptiveTdee(consumptions, weights), [consumptions, weights]);

  const model = useMemo(() => {
    if (scopedC.length === 0 || scopedW.length < 2 || tdeeKcal === 0) return null;
    const dailyIntake = aggregateBy(
      scopedC,
      (c) => c.datetime,
      'day',
      (g) => round(g.reduce((s, c) => s + consumptionNutrients(c).kcal, 0)),
    ).map((b) => ({ date: b.start, kcal: b.value }));

    const corr = deficitVsWeightChange(dailyIntake, tdeeKcal, scopedW, granularity);

    // Fusion des deux séries de perte par date pour l'overlay.
    const predicted = predictedLossSeries(dailyIntake, tdeeKcal);
    const actual = actualLossSeries(scopedW, 7);
    const byDate = new Map<
      string,
      { date: string; predictedLossKg: number; actualLossKg: number }
    >();
    for (const p of predicted)
      byDate.set(p.date, { date: p.date, predictedLossKg: p.predictedLossKg, actualLossKg: NaN });
    for (const a of actual) {
      const row = byDate.get(a.date) ?? { date: a.date, predictedLossKg: NaN, actualLossKg: NaN };
      row.actualLossKg = a.actualLossKg;
      byDate.set(a.date, row);
    }
    const reconciliation = [...byDate.values()].sort((x, y) => x.date.localeCompare(y.date));

    const deficitBreakdown = cumulativeDeficitBreakdownSeries(
      dailyIntake,
      dailyBurned(scopedE),
      tdeeKcal,
    );

    return { corr, reconciliation, deficitBreakdown };
  }, [scopedC, scopedW, scopedE, tdeeKcal, granularity]);

  return (
    <>
      <div className="page-header">
        <div>
          <h1>Analyse</h1>
          <p>Le lien entre ce que tu manges et l'évolution de ton poids.</p>
        </div>
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
          <PeriodPicker value={period} onChange={setPeriod} />
          <GranularityPicker value={granularity} onChange={setGranularity} />
        </div>
      </div>

      {!model ? (
        <EmptyState>Il faut au moins quelques pesées et consommations pour analyser.</EmptyState>
      ) : (
        <>
          <div className="grid kpis" style={{ marginBottom: '1rem' }}>
            <Kpi
              label="Corrélation bilan↔poids"
              value={correlationLabel(model.corr.correlation)}
              hint="lien entre bilan énergétique et variation de poids"
            />
            <Kpi label="Périodes analysées" value={model.corr.points.length} />
            <Kpi
              label="TDEE théorique"
              value={Math.round(tdeeKcal)}
              unit="kcal"
              hint="formule Mifflin-St Jeor × activité"
            />
            <Kpi
              label="TDEE réel (observé)"
              value={adaptive ? adaptive.tdeeKcal : '—'}
              unit={adaptive ? 'kcal' : ''}
              delta={
                adaptive
                  ? { value: adaptive.tdeeKcal - Math.round(tdeeKcal), unit: ' vs théorique' }
                  : undefined
              }
              hint={
                adaptive
                  ? `sur ${adaptive.days} j${adaptive.reliable ? '' : ' · échantillon court'}`
                  : 'besoin de ≥ 2 pesées et d’apports saisis'
              }
            />
          </div>
          {adaptive?.reliable && Math.abs(adaptive.tdeeKcal - tdeeKcal) >= 150 && (
            <p className="muted" style={{ fontSize: '0.85rem', marginBottom: '1rem' }}>
              💡 Ta maintenance réelle observée ({adaptive.tdeeKcal} kcal) diffère de l'estimation
              théorique ({Math.round(tdeeKcal)} kcal). Si ta progression stagne ou s'emballe, ajuste
              ton seuil calorique en te basant sur cette valeur empirique.
            </p>
          )}

          <div className="grid charts">
            <ChartCard
              title="Bilan énergétique vs variation de poids"
              subtitle="chaque point = une période ; déficit → perte, surplus → prise"
            >
              <CorrelationScatter data={model.corr.points} palette={palette} />
            </ChartCard>
            <ChartCard
              title="Variation théorique vs réelle"
              subtitle={
                phase === 'cut'
                  ? "théorique = d'après le bilan énergétique · réelle = pesées lissées"
                  : 'variation réelle (pesées lissées) · pas de projection kg hors sèche'
              }
            >
              <LossReconciliationChart
                data={model.reconciliation}
                phase={phase}
                palette={palette}
              />
            </ChartCard>
            <ChartCard
              title="Bilan énergétique cumulé"
              subtitle="alimentaire (maintenance − apport) · sport (dépense en plus) · total"
            >
              <DeficitBreakdownChart data={model.deficitBreakdown} palette={palette} />
            </ChartCard>
          </div>
        </>
      )}
    </>
  );
}
